import { useCallback, useEffect, useState } from 'react';

import { recordAndroidDismiss, recordInstalled } from '@/lib/pwa/install-storage';
import { analytics } from '@/lib/analytics/analytics';
import {
  ANALYTICS_EVENT,
} from '@/lib/analytics/types/analytics-enums';
import * as log from '@/lib/monitoring/logger';
import type {
  BeforeInstallPromptEvent,
  InstallPromptApi,
} from './types/use-install-prompt-types';
import { InstallPromptOutcome } from '@/lib/pwa/types/pwa-enums';

export function useInstallPrompt(): InstallPromptApi {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | undefined>();

  useEffect(() => {
    const handleBeforeInstallPrompt = (event: Event) => {
      event.preventDefault();
      setDeferredPrompt(event as BeforeInstallPromptEvent);
    };

    const handleInstalled = () => {
      recordInstalled();
      setDeferredPrompt(undefined);
      analytics.track(ANALYTICS_EVENT.PWA_INSTALLED, { platform: 'android' });
      log.info('PWA installed', { platform: 'android' });
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleInstalled);
    };
  }, []);

  const prompt = useCallback(async (): Promise<InstallPromptOutcome | undefined> => {
    if (!deferredPrompt) return undefined;

    try {
      await deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice;
      const outcome =
        choice.outcome === 'accepted'
          ? InstallPromptOutcome.ACCEPTED
          : InstallPromptOutcome.DISMISSED;

      analytics.track(ANALYTICS_EVENT.PWA_INSTALL_PROMPT_OUTCOME, {
        outcome,
        platform: choice.platform,
      });

      // Chrome only fires beforeinstallprompt once per session; clear the cached event.
      setDeferredPrompt(undefined);
      return outcome;
    } catch (err) {
      const errorInstance = err instanceof Error ? err : new Error(String(err));
      log.error('PWA install prompt failed', errorInstance);
      return undefined;
    }
  }, [deferredPrompt]);

  const dismissForNow = useCallback(() => {
    recordAndroidDismiss();
    setDeferredPrompt(undefined);
    analytics.track(ANALYTICS_EVENT.PWA_INSTALL_PROMPT_DISMISSED, { platform: 'android' });
  }, []);

  return {
    canPrompt: deferredPrompt !== undefined,
    prompt,
    dismissForNow,
  };
}
